import type { User } from '@/@types/user'
import { USER_PROFILE } from '@/constants/api'
import storage from '@/utils/Storage'
import { useIsMobile } from '@/utils/hooks/useMediaQuery'
import { currentUserQueryOptions } from '@/utils/fetchers/user'
import queryClient from '@/utils/queryClient'
import { generalAxiosInstance } from '@/utils/request'
import { DEFAULT_REDIRECT_URL, normalizeRedirectURL } from '@/utils/redirectURL'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { isNil } from 'lodash-es'
import { motion } from 'motion/react'
import { useTitle } from 'react-use'
import { z } from 'zod'
import DesktopLoginSection from './-components/DesktopLoginSection'
import MobileLoginSection from './-components/MobileLoginSection'
import styles from './index.module.css'

export const Route = createFileRoute('/login/')({
	component: LoginPage,
	validateSearch: z.object({
		redirectURL: z.string().optional(),
	}),
	beforeLoad: async ({ search }) => {
		const user = await queryClient.ensureQueryData<User | null>({
			queryKey: currentUserQueryOptions.queryKey,
			queryFn: async () =>
				generalAxiosInstance
					.get(USER_PROFILE, {
						headers: {
							'x-jike-access-token': storage.auth.token,
						},
					})
					.then((res) => res.data.user as User)
					.catch(() => null),
		})
		const redirectURL = normalizeRedirectURL(search.redirectURL)

		if (!isNil(user)) {
			throw redirect({ href: redirectURL, replace: true } as never)
		}
	},
})

export default function LoginPage() {
	useTitle(`登录 - 即刻`)

	const isMobile = useIsMobile()
	const { redirectURL: redirectURLInSearch } = Route.useSearch()
	const redirectURL = normalizeRedirectURL(redirectURLInSearch ?? DEFAULT_REDIRECT_URL)

	return (
		<motion.div
			className={styles.container}
			initial={{
				opacity: 0,
			}}
			animate={{
				opacity: 1,
				transition: {
					delay: 0.3,
				},
			}}
			exit={{
				opacity: 0,
			}}
		>
			{isMobile ? <MobileLoginSection redirectURL={redirectURL} /> : <DesktopLoginSection />}
		</motion.div>
	)
}
